import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SiretLookup from './SiretLookup';
import './BasePerso.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const calculateEndTime = (startTime) => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const endHours = hours + 1;
  return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const statutLabels = {
  nouveau: 'Nouveau',
  a_rappeler: 'A rappeler',
  interesse: 'Intéressé',
  pas_interesse: 'Pas intéressé',
  rdv_pris: 'RDV pris',
  ne_pas_rappeler: 'Ne pas rappeler'
};

function BasePerso({ currentUser }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterActivite, setFilterActivite] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ byStatut: [], byActivite: [] });
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState('');
  const [detailFiche, setDetailFiche] = useState(null);
  const fileInputRef = useRef(null);
  const isAdmin = currentUser?.role === 'admin';

  // RDV Modal state
  const [showModal, setShowModal] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [rdvCreated, setRdvCreated] = useState({});
  const [currentRdvFiche, setCurrentRdvFiche] = useState(null);
  const [formData, setFormData] = useState({
    commercial_id: '',
    titre: '',
    description: '',
    client_nom: '',
    client_telephone: '',
    client_adresse: '',
    date_rdv: '',
    heure_debut: '09:00',
    heure_fin: '10:00',
    statut: 'planifie'
  });

  const ITEMS_PER_PAGE = 50;

  // Charger les commerciaux au mount
  useEffect(() => {
    const fetchCommerciaux = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/planning/commerciaux`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCommerciaux(response.data);
      } catch (err) {
        console.error('Erreur chargement commerciaux:', err);
      }
    };
    fetchCommerciaux();
  }, []);

  // Charger les fiches
  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (searchText) params.append('search', searchText);
      if (filterStatut) params.append('statut', filterStatut);
      if (filterActivite) params.append('activite', filterActivite);
      if (isAdmin && filterUserId) params.append('user_id', filterUserId);

      const response = await axios.get(`${API_URL}/api/base-perso?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data.results);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
      setStats(response.data.stats);
      if (response.data.users) setUsers(response.data.users);
    } catch (err) {
      console.error('Erreur chargement base perso:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => {
    fetchData(1);
  };

  // Import CSV
  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/base-perso/import`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setImportResult(response.data);
      fetchData(1);
    } catch (err) {
      console.error('Erreur import CSV:', err);
      setImportResult({ error: err.response?.data?.error || 'Erreur lors de l\'import' });
    }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Changer statut
  const handleStatutChange = async (id, newStatut) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/base-perso/statut`, { id, statut: newStatut }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(prev => prev.map(r => r.id === id ? { ...r, statut: newStatut } : r));
      if (detailFiche && detailFiche.id === id) {
        setDetailFiche(prev => ({ ...prev, statut: newStatut }));
      }
    } catch (err) {
      console.error('Erreur changement statut:', err);
    }
  };

  // Supprimer une fiche
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette fiche ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/base-perso/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData(currentPage);
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  // Purger toute la base
  const handlePurge = async () => {
    if (!window.confirm('Vider TOUTE votre base personnelle ? Cette action est irréversible.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/base-perso/purge`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData(1);
    } catch (err) {
      console.error('Erreur purge:', err);
    }
  };

  // Ouvrir modal RDV
  const handleOpenRdvModal = (fiche) => {
    setCurrentRdvFiche(fiche);
    setFormData({
      commercial_id: commerciaux[0]?.id || '',
      titre: `RDV ${fiche.activite || ''} - ${fiche.nom}`,
      description: `Source: Base Perso\n${fiche.entreprise ? `Entreprise: ${fiche.entreprise}` : ''}${fiche.email ? `\nEmail: ${fiche.email}` : ''}${fiche.notes ? `\nNotes: ${fiche.notes}` : ''}`,
      client_nom: fiche.nom,
      client_telephone: fiche.telephone || '',
      client_adresse: fiche.adresse || '',
      date_rdv: new Date().toISOString().split('T')[0],
      heure_debut: '09:00',
      heure_fin: '10:00',
      statut: 'planifie'
    });
    setShowModal(true);
  };

  // Soumettre RDV
  const handleSubmitRdv = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        heure_fin: calculateEndTime(formData.heure_debut)
      };
      await axios.post(`${API_URL}/api/planning/rdv`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      if (currentRdvFiche) {
        setRdvCreated(prev => ({ ...prev, [currentRdvFiche.id]: true }));
        // Auto-update statut to rdv_pris
        handleStatutChange(currentRdvFiche.id, 'rdv_pris');
      }
    } catch (err) {
      console.error('Erreur creation RDV:', err);
      alert(err.response?.data?.error || 'Erreur lors de la création du RDV');
    }
  };

  return (
    <div className="base-perso">
      <div className="base-perso-header">
        <h2>Ma Base Perso</h2>
        <p className="base-perso-subtitle">
          Importez votre propre fichier CSV et gérez vos contacts personnels | <strong>{total}</strong> fiche{total > 1 ? 's' : ''}
        </p>
      </div>

      {/* Zone Import CSV */}
      <div className="base-perso-import">
        <div className="import-zone">
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            ref={fileInputRef}
            onChange={handleImportCSV}
            style={{ display: 'none' }}
          />
          <button
            className="btn-import-csv"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? (
              <><span className="spinner-small"></span> Import en cours...</>
            ) : (
              <>Importer CSV / Excel</>
            )}
          </button>
          {total > 0 && (
            <button className="btn-purge" onClick={handlePurge}>
              Vider ma base
            </button>
          )}
        </div>
        {importResult && !importResult.error && (
          <div className="import-result success">
            {importResult.imported} importé{importResult.imported > 1 ? 's' : ''} | {importResult.duplicates} doublon{importResult.duplicates > 1 ? 's' : ''} | {importResult.errors} erreur{importResult.errors > 1 ? 's' : ''}
          </div>
        )}
        {importResult && importResult.error && (
          <div className="import-result error">{importResult.error}</div>
        )}
      </div>

      {/* Filtres */}
      <div className="base-perso-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Rechercher (nom, tel, email, entreprise, ville)..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className="filter-select">
            <option value="">Tous les statuts</option>
            {Object.entries(statutLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        {stats.byActivite && stats.byActivite.length > 0 && (
          <div className="filter-group">
            <select value={filterActivite} onChange={e => setFilterActivite(e.target.value)} className="filter-select">
              <option value="">Toutes activités</option>
              {stats.byActivite.map(a => (
                <option key={a.activite} value={a.activite}>{a.activite} ({a.count})</option>
              ))}
            </select>
          </div>
        )}
        {isAdmin && users.length > 0 && (
          <div className="filter-group">
            <select value={filterUserId} onChange={e => setFilterUserId(e.target.value)} className="filter-select">
              <option value="">Tous les utilisateurs</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.user_nom || `User #${u.user_id}`}</option>
              ))}
            </select>
          </div>
        )}
        <button className="btn-search" onClick={handleSearch}>
          Consulter
        </button>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="loading-state">
          <span className="spinner-small"></span> Chargement...
        </div>
      ) : results.length > 0 ? (
        <>
          <div className="table-container">
            <table className="base-perso-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Téléphone</th>
                  <th className="td-entreprise">Entreprise</th>
                  <th className="td-ville">Ville</th>
                  {isAdmin && <th className="td-user">Utilisateur</th>}
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.id}>
                    <td className="td-nom">{r.nom}</td>
                    <td className="td-tel">
                      {r.telephone ? (
                        <a href={`tel:${r.telephone}`} className="phone-link">{r.telephone}</a>
                      ) : '-'}
                    </td>
                    <td className="td-entreprise">{r.entreprise || '-'}</td>
                    <td className="td-ville">{r.ville || '-'}</td>
                    {isAdmin && <td className="td-user"><span className="user-badge">{r.user_nom || `#${r.user_id}`}</span>{r.deleted_by_user === 1 && <span className="badge-deleted">Supprimé</span>}</td>}
                    <td>
                      <select
                        className={`statut-select statut-${r.statut || 'nouveau'}`}
                        value={r.statut || 'nouveau'}
                        onChange={e => handleStatutChange(r.id, e.target.value)}
                      >
                        {Object.entries(statutLabels).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="td-actions">
                      <button className="btn-voir-fiche" onClick={() => setDetailFiche(r)}>Voir</button>
                      {rdvCreated[r.id] ? (
                        <span className="badge-added">RDV</span>
                      ) : (
                        <button className="btn-add-rdv" onClick={() => handleOpenRdvModal(r)}>RDV</button>
                      )}
                      <button className="btn-delete-fiche" onClick={() => handleDelete(r.id)}>X</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Modal détail fiche */}
          {detailFiche && (
            <div className="modal-overlay">
              <div className="modal-content planning-modal">
                <div className="modal-header">
                  <h3>{detailFiche.nom}</h3>
                  <button className="btn-close" onClick={() => setDetailFiche(null)}>&times;</button>
                </div>
                <div className="fiche-detail-body">
                  {isAdmin && (
                    <div className="fiche-detail-row">
                      <span className="fiche-label">Utilisateur</span>
                      <span className="fiche-value">
                        <span className="user-badge-modal">{detailFiche.user_nom || `#${detailFiche.user_id}`}</span>
                        {detailFiche.deleted_by_user === 1 && <span className="badge-deleted-modal">Supprimé par l'utilisateur</span>}
                      </span>
                    </div>
                  )}
                  {detailFiche.entreprise && (
                    <div className="fiche-detail-row">
                      <span className="fiche-label">Entreprise</span>
                      <span className="fiche-value">{detailFiche.entreprise}</span>
                    </div>
                  )}
                  {detailFiche.activite && (
                    <div className="fiche-detail-row">
                      <span className="fiche-label">Activité</span>
                      <span className="fiche-value">{detailFiche.activite}</span>
                    </div>
                  )}
                  <div className="fiche-detail-row">
                    <span className="fiche-label">Téléphone</span>
                    <span className="fiche-value">
                      {detailFiche.telephone ? (
                        <a href={`tel:${detailFiche.telephone}`} className="phone-link">{detailFiche.telephone}</a>
                      ) : '-'}
                    </span>
                  </div>
                  {detailFiche.email && (
                    <div className="fiche-detail-row">
                      <span className="fiche-label">Email</span>
                      <span className="fiche-value">
                        <a href={`mailto:${detailFiche.email}`} className="web-link">{detailFiche.email}</a>
                      </span>
                    </div>
                  )}
                  <div className="fiche-detail-row">
                    <span className="fiche-label">Adresse</span>
                    <span className="fiche-value">{detailFiche.adresse || '-'}</span>
                  </div>
                  <div className="fiche-detail-row">
                    <span className="fiche-label">Ville</span>
                    <span className="fiche-value">{detailFiche.ville || '-'}</span>
                  </div>
                  <div className="fiche-detail-row">
                    <span className="fiche-label">Code postal</span>
                    <span className="fiche-value">{detailFiche.code_postal || '-'}</span>
                  </div>
                  {detailFiche.site_web && (
                    <div className="fiche-detail-row">
                      <span className="fiche-label">Site web</span>
                      <span className="fiche-value">
                        <a href={detailFiche.site_web.startsWith('http') ? detailFiche.site_web : `https://${detailFiche.site_web}`} target="_blank" rel="noopener noreferrer" className="web-link">{detailFiche.site_web}</a>
                      </span>
                    </div>
                  )}
                  {detailFiche.notes && (
                    <div className="fiche-detail-row">
                      <span className="fiche-label">Notes</span>
                      <span className="fiche-value">{detailFiche.notes}</span>
                    </div>
                  )}
                  <div className="fiche-detail-row">
                    <span className="fiche-label">Statut</span>
                    <span className="fiche-value">
                      <select
                        className={`statut-select statut-${detailFiche.statut || 'nouveau'}`}
                        value={detailFiche.statut || 'nouveau'}
                        onChange={e => handleStatutChange(detailFiche.id, e.target.value)}
                      >
                        {Object.entries(statutLabels).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </span>
                  </div>
                  <div className="fiche-detail-actions">
                    {!rdvCreated[detailFiche.id] && (
                      <button className="btn-add-rdv" onClick={() => { handleOpenRdvModal(detailFiche); setDetailFiche(null); }}>
                        Prendre RDV
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => fetchData(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
                Précédent
              </button>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                let page;
                if (totalPages <= 10) page = i + 1;
                else if (currentPage <= 5) page = i + 1;
                else if (currentPage >= totalPages - 4) page = totalPages - 9 + i;
                else page = currentPage - 4 + i;
                return (
                  <button key={page} className={currentPage === page ? 'active' : ''} onClick={() => fetchData(page)}>
                    {page}
                  </button>
                );
              })}
              <button onClick={() => fetchData(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
                Suivant
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="no-results">
          {total === 0
            ? 'Aucune fiche dans votre base. Importez un fichier CSV pour commencer.'
            : 'Aucun résultat pour ces filtres.'}
        </div>
      )}

      {/* Modal Nouveau RDV */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content planning-modal">
            <div className="modal-header">
              <h3>Nouveau Rendez-vous</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitRdv}>
              <SiretLookup
                compact
                onCompanyFound={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    client_nom: data.dirigeant_nom || prev.client_nom,
                    client_adresse: data.adresse || prev.client_adresse,
                    titre: prev.titre || `RDV ${data.entreprise}`,
                    description: prev.description || `Entreprise: ${data.entreprise}${data.dirigeant_qualite ? `\nDirigeant: ${data.dirigeant_nom} (${data.dirigeant_qualite})` : ''}`
                  }));
                }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Commercial *</label>
                  <select
                    value={formData.commercial_id}
                    onChange={e => setFormData({...formData, commercial_id: parseInt(e.target.value)})}
                    required
                  >
                    {commerciaux.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.statut}
                    onChange={e => setFormData({...formData, statut: e.target.value})}
                  >
                    <option value="planifie">Planifié</option>
                    {(currentUser?.role === 'admin' || currentUser?.id === 2) && (
                      <option value="confirme">Confirmé</option>
                    )}
                    <option value="annule">Annulé</option>
                    <option value="termine">Terminé</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Titre du RDV *</label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={e => setFormData({...formData, titre: e.target.value})}
                  placeholder="Ex: Presentation produit, Signature contrat..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date_rdv}
                    onChange={e => setFormData({...formData, date_rdv: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure *</label>
                  <input
                    type="time"
                    value={formData.heure_debut}
                    onChange={e => setFormData({...formData, heure_debut: e.target.value})}
                    required
                  />
                  <small className="form-hint">Durée: 1h</small>
                </div>
              </div>

              <div className="form-group">
                <label>Nom du client</label>
                <input
                  type="text"
                  value={formData.client_nom}
                  onChange={e => setFormData({...formData, client_nom: e.target.value})}
                  placeholder="Nom du client ou prospect"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone client</label>
                  <input
                    type="tel"
                    value={formData.client_telephone}
                    onChange={e => setFormData({...formData, client_telephone: e.target.value})}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input
                    type="text"
                    value={formData.client_adresse}
                    onChange={e => setFormData({...formData, client_adresse: e.target.value})}
                    placeholder="Lieu du rendez-vous"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description / Notes</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Détails du rendez-vous..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer le RDV
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BasePerso;
